import type { EmployeeModuleKey, RolePreset, PresetPermissions } from './types';
import { normalizeModuleKeyForDb } from './modules';
import {
  PRESET_AGENDA_ADMIN,
  PRESET_AGENDA_EDIT_ALL,
  PRESET_AGENDA_OWN,
  PRESET_CASH_OPERATE,
  PRESET_EDIT,
  PRESET_KITCHEN_STAFF,
  PRESET_NONE,
  PRESET_ORDERS_OPERATE,
  PRESET_POS_EDIT_VOID,
  PRESET_POS_FULL,
  PRESET_POS_SELL,
  PRESET_PURCHASES_ADMIN,
  PRESET_PURCHASES_AUDIT,
  PRESET_PURCHASES_STOCK_LEAD,
  PRESET_PURCHASES_SUPERVISOR,
  PRESET_PURCHASES_VIEW,
  PRESET_REPORTS_AUDIT,
  PRESET_TABLES_MANAGE,
  PRESET_TABLES_OPERATE,
  PRESET_VIEW,
  allNoneExcept,
} from './presetLevels';

export const ROLE_PRESETS: RolePreset[] = [
  {
    id: 'pos-only',
    label: 'Solo POS',
    description: 'Cobra en el POS. Sin anular ni descontar hasta que el dueño los active.',
    permissions: allNoneExcept({ POS: PRESET_POS_SELL }),
  },
  {
    id: 'cashier',
    label: 'Cajero',
    description: 'POS completo y caja. Ve catálogo, stock, clientes, promos y gastos.',
    permissions: allNoneExcept({
      POS: PRESET_POS_FULL,
      Cash: PRESET_CASH_OPERATE,
      Checks: PRESET_EDIT,
      Catalog: PRESET_VIEW,
      Inventory: PRESET_VIEW,
      Fiado: PRESET_VIEW,
      Reports: PRESET_VIEW,
      Promotions: PRESET_VIEW,
      Expenses: PRESET_VIEW,
      Purchases: PRESET_PURCHASES_VIEW,
      Orders: PRESET_VIEW,
    }),
  },
  {
    id: 'waiter',
    label: 'Mozo / Mesas',
    description: 'Maneja mesas y pedidos. Sin caja ni costos.',
    permissions: allNoneExcept({
      Tables: PRESET_TABLES_OPERATE,
      Orders: PRESET_ORDERS_OPERATE,
      POS: PRESET_POS_SELL,
      MenuOptions: PRESET_VIEW,
    }),
  },
  {
    id: 'kitchen',
    label: 'Cocina',
    description: 'Cola de cocina: ve y actualiza estados. Sin precios ni datos de cliente.',
    permissions: allNoneExcept({
      Kitchen: PRESET_KITCHEN_STAFF,
      Orders: PRESET_VIEW,
    }),
  },
  {
    id: 'mostrador',
    label: 'Mostrador',
    description: 'Cobra en el POS, ve pedidos y ventas. Sin caja ni catálogo.',
    permissions: allNoneExcept({
      POS: PRESET_POS_SELL,
      Orders: PRESET_VIEW,
    }),
  },
  {
    id: 'agenda-staff',
    label: 'Personal de turnos',
    description: 'Maneja solo su agenda. No ve las de otros.',
    permissions: allNoneExcept({
      Agenda: PRESET_AGENDA_OWN,
      Reports: PRESET_VIEW,
    }),
  },
  {
    id: 'receptionist',
    label: 'Recepción',
    description: 'Maneja las agendas de todos. Sin POS ni caja.',
    permissions: allNoneExcept({
      Agenda: PRESET_AGENDA_ADMIN,
      Reports: PRESET_VIEW,
      Catalog: PRESET_VIEW,
      Fiado: PRESET_VIEW,
    }),
  },
  {
    id: 'stock-lead',
    label: 'Encargado stock',
    description: 'Stock y catálogo del día. Sin caja ni ajustes de sucursal.',
    permissions: allNoneExcept({
      Inventory: PRESET_EDIT,
      Catalog: PRESET_EDIT,
      MenuOptions: PRESET_EDIT,
      Reports: PRESET_VIEW,
      POS: PRESET_VIEW,
      Purchases: PRESET_PURCHASES_STOCK_LEAD,
    }),
  },
  {
    id: 'supervisor',
    label: 'Supervisor',
    description: 'Usa el día a día y puede anular. Ve ajustes. No edita equipo ni integraciones.',
    permissions: allNoneExcept({
      POS: PRESET_POS_FULL,
      Cash: { ...PRESET_CASH_OPERATE, canVoid: true, canViewOthers: true },
      Checks: { ...PRESET_EDIT, actions: { endorse: true, reject: true } },
      Orders: PRESET_EDIT,
      Tables: PRESET_TABLES_MANAGE,
      Kitchen: PRESET_EDIT,
      MenuOptions: PRESET_EDIT,
      Catalog: PRESET_EDIT,
      Inventory: PRESET_EDIT,
      Fiado: PRESET_EDIT,
      Reports: PRESET_EDIT,
      BranchSettings: PRESET_VIEW,
      Promotions: PRESET_EDIT,
      Expenses: PRESET_POS_EDIT_VOID,
      Purchases: PRESET_PURCHASES_SUPERVISOR,
      Agenda: PRESET_AGENDA_EDIT_ALL,
    }),
  },
  {
    id: 'branch-admin',
    label: 'Admin sucursal',
    description: 'Todo en la sucursal, incluso ajustes. Organización y plan y facturación: solo el dueño.',
    permissions: allNoneExcept({
      POS: PRESET_POS_FULL,
      Cash: { ...PRESET_EDIT, canVoid: true, canViewOthers: true },
      Checks: { ...PRESET_EDIT, actions: { endorse: true, reject: true } },
      Orders: PRESET_EDIT,
      Tables: PRESET_TABLES_MANAGE,
      Kitchen: PRESET_EDIT,
      MenuOptions: PRESET_EDIT,
      Catalog: PRESET_EDIT,
      Inventory: PRESET_EDIT,
      Fiado: PRESET_EDIT,
      Reports: PRESET_EDIT,
      BranchSettings: {
        ...PRESET_EDIT,
        actions: {
          edit_commercial: true,
          edit_pos_rules: true,
        },
      },
      Promotions: PRESET_EDIT,
      Expenses: PRESET_POS_EDIT_VOID,
      Purchases: PRESET_PURCHASES_ADMIN,
      Agenda: PRESET_AGENDA_ADMIN,
      Team: {
        ...PRESET_VIEW,
        actions: {
          invite: true,
          assign_branches: true,
          edit_permissions: true,
          revoke: true,
          history: true,
          export: true,
        },
      },
      Organization: PRESET_VIEW,
      Billing: PRESET_VIEW,
      Support: {
        ...PRESET_VIEW,
        actions: { view_branch_tickets: true },
      },
    }),
  },
  {
    id: 'auditor',
    label: 'Auditor',
    description: 'Solo ve informes y puede exportar. No cambia nada.',
    permissions: allNoneExcept({
      Reports: PRESET_REPORTS_AUDIT,
      Cash: PRESET_VIEW,
      Checks: PRESET_VIEW,
      Expenses: PRESET_VIEW,
      Inventory: PRESET_VIEW,
      Fiado: PRESET_VIEW,
      POS: PRESET_VIEW,
      Catalog: PRESET_VIEW,
      Purchases: PRESET_PURCHASES_AUDIT,
    }),
  },
];

/** @deprecated Use branch-admin; kept for invitation payloads that still send "manager". */
export const LEGACY_MANAGER_PRESET_ID = 'manager';

export const ROLE_LABELS_ES: Record<string, string> = {
  'pos-only': 'Solo POS',
  cashier: 'Cajero',
  waiter: 'Mozo / Mesas',
  kitchen: 'Cocina',
  mostrador: 'Mostrador',
  'agenda-staff': 'Personal de turnos',
  receptionist: 'Recepción',
  'stock-lead': 'Encargado stock',
  supervisor: 'Supervisor',
  manager: 'Admin sucursal',
  'branch-admin': 'Admin sucursal',
  auditor: 'Auditor',
};

export function resolvePresetId(roleId: string | null | undefined): string {
  if (!roleId) return 'cashier';
  if (roleId === LEGACY_MANAGER_PRESET_ID || roleId === 'gerente') return 'branch-admin';
  return roleId;
}

export function findRolePreset(roleId: string | null | undefined): RolePreset | undefined {
  const id = resolvePresetId(roleId);
  const byId = ROLE_PRESETS.find((p) => p.id === id);
  if (byId) return byId;
  const needle = (roleId ?? '').trim().toLowerCase();
  if (!needle) return undefined;
  return ROLE_PRESETS.find((p) => p.label.toLowerCase() === needle);
}

export function invitationRoleLabel(role: string | null | undefined): string {
  if (!role?.trim()) return 'Cajero';
  if (ROLE_LABELS_ES[role]) return ROLE_LABELS_ES[role];
  const byLabel = Object.values(ROLE_LABELS_ES).find((l) => l.toLowerCase() === role.toLowerCase());
  if (byLabel) return byLabel;
  if (role.toLowerCase() === 'gerente') return 'Admin sucursal';
  return role;
}

/** Fallback when invitation JSON has no permissions (accept + portal). */
export function resolvePresetPermissions(
  role: string | null | undefined
): Partial<Record<EmployeeModuleKey, PresetPermissions>> {
  const preset = findRolePreset(role);
  if (preset) return preset.permissions;
  const n = (role ?? '').toLowerCase();
  if (n.includes('supervisor')) return ROLE_PRESETS.find((p) => p.id === 'supervisor')!.permissions;
  if (
    n.includes('admin sucursal') ||
    n.includes('branch-admin') ||
    n.includes('gerente') ||
    n.includes('manager')
  ) {
    return ROLE_PRESETS.find((p) => p.id === 'branch-admin')!.permissions;
  }
  if (n.includes('mozo') || n.includes('mesas') || n.includes('waiter')) {
    return ROLE_PRESETS.find((p) => p.id === 'waiter')!.permissions;
  }
  if (n.includes('cocina') || n.includes('kitchen')) {
    return ROLE_PRESETS.find((p) => p.id === 'kitchen')!.permissions;
  }
  if (n.includes('mostrador')) {
    return ROLE_PRESETS.find((p) => p.id === 'mostrador')!.permissions;
  }
  if (n.includes('stock') || n.includes('encargado')) {
    return ROLE_PRESETS.find((p) => p.id === 'stock-lead')!.permissions;
  }
  if (n.includes('auditor')) return ROLE_PRESETS.find((p) => p.id === 'auditor')!.permissions;
  if (n.includes('turnos') || n.includes('agenda-staff') || n === 'agenda-staff') {
    return ROLE_PRESETS.find((p) => p.id === 'agenda-staff')!.permissions;
  }
  if (n.includes('recepción') || n.includes('recepcionista') || n === 'receptionist') {
    return ROLE_PRESETS.find((p) => p.id === 'receptionist')!.permissions;
  }
  if ((n.includes('solo') && n.includes('pos')) || n === 'pos-only') {
    return ROLE_PRESETS.find((p) => p.id === 'pos-only')!.permissions;
  }
  if (n.includes('cajero') || n.includes('cashier')) {
    return ROLE_PRESETS.find((p) => p.id === 'cashier')!.permissions;
  }
  return ROLE_PRESETS.find((p) => p.id === 'cashier')!.permissions;
}

export function presetToApiPayload(
  preset: RolePreset,
  visibleModules: readonly EmployeeModuleKey[]
): Record<string, PresetPermissions> {
  const result: Record<string, PresetPermissions> = {};
  for (const module of visibleModules) {
    // Fase C: 'Customers' → 'Fiado' for DB lookup in presets
    const dbKey = normalizeModuleKeyForDb(module) as EmployeeModuleKey;
    result[module] = preset.permissions[dbKey] ?? preset.permissions[module] ?? PRESET_NONE;
  }
  return result;
}

export function presetPermissionsForModules(
  source: Partial<Record<EmployeeModuleKey, PresetPermissions>>
): Record<string, PresetPermissions> {
  const result: Record<string, PresetPermissions> = {};
  for (const module of Object.keys(source) as EmployeeModuleKey[]) {
    result[module] = source[module] ?? PRESET_NONE;
  }
  return result;
}
